/**
 * API utility — centralized fetch wrapper.
 * Ported from the Receipts app (client/src/utils/api.js). Auth is carried by an
 * HttpOnly Secure cookie the server sets on login, so requests just send
 * credentials; there is no token in JS. On a 401 the user is bounced to /login.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/**
 * Global 401 handler — redirect to /login. Skips the /auth/me probe, which
 * AuthContext handles on mount.
 */
function handleUnauthorized(endpoint) {
  if (endpoint === '/auth/me') return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function request(endpoint, options = {}) {
  const config = {
    credentials: 'include', // send the HttpOnly auth cookie with every request
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    if (response.status === 401) {
      handleUnauthorized(endpoint);
    }
    throw new ApiError(error.error || 'Request failed', response.status, error.details);
  }

  // 204 No Content (e.g. DELETE) has no body — parsing it as JSON throws and
  // would surface as a false error even though the request succeeded.
  if (response.status === 204) return null;

  return response.json();
}

const api = {
  get: (endpoint) => request(endpoint),
  post: (endpoint, data) => request(endpoint, { method: 'POST', body: data }),
  put: (endpoint, data) => request(endpoint, { method: 'PUT', body: data }),
  patch: (endpoint, data) => request(endpoint, { method: 'PATCH', body: data }),
  delete: (endpoint, data) => request(endpoint, { method: 'DELETE', body: data }),
};

/**
 * Absolute URL for an API endpoint under the same base the fetch wrapper uses.
 * For file-download endpoints (e.g. /maps/:id/export.csv) the browser navigates
 * to this directly — the server's Content-Disposition triggers the download and
 * the HttpOnly auth cookie rides along automatically on the same-origin request,
 * so there's nothing to add to headers here.
 *
 * @param {string} endpoint - e.g. `/maps/abc/export.csv`
 * @returns {string}
 */
function apiUrl(endpoint) {
  return `${API_BASE}${endpoint}`;
}

/**
 * Trigger a browser file download for a GET endpoint by clicking a transient
 * anchor. Used by the export menu (CSV/GeoJSON/Google Ads/Meta pins). An anchor
 * click (rather than window.location =) keeps the current SPA route intact and
 * lets the browser handle the attachment response. `download` hints a filename;
 * the server's Content-Disposition still wins when present.
 *
 * @param {string} endpoint - download endpoint under the API base
 */
function downloadFromApi(endpoint) {
  const a = document.createElement('a');
  a.href = apiUrl(endpoint);
  a.rel = 'noopener';
  // Same-origin download; the server sets the real filename via Content-Disposition.
  a.setAttribute('download', '');
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Save a Blob to disk via a transient object-URL anchor click. Used when the file
 * arrives from a fetch() (e.g. a POST response) rather than a plain GET the browser
 * can navigate to. The object URL is revoked after the click to free memory.
 *
 * @param {Blob} blob - the file contents
 * @param {string} filename - suggested filename
 */
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * POST to an endpoint and download the (binary) response as a file — for exports
 * whose route is POST because it takes an optional body (e.g. the PDF export,
 * which accepts an optional client-captured map PNG). The HttpOnly auth cookie
 * rides along via credentials:'include'. `body` may be a Blob/ArrayBuffer/typed
 * array (sent as-is with the given contentType) or omitted for an empty POST.
 * Throws ApiError on a non-2xx response so callers can toast a failure.
 *
 * @param {string} endpoint - download endpoint under the API base
 * @param {{ body?: BodyInit, contentType?: string, filename?: string }} [opts]
 * @returns {Promise<void>}
 */
async function postForDownload(endpoint, opts = {}) {
  const headers = {};
  if (opts.contentType) headers['Content-Type'] = opts.contentType;
  const response = await fetch(apiUrl(endpoint), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: opts.body,
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    const err = await response.json().catch(() => ({ error: 'Export failed' }));
    throw new ApiError(err.error || 'Export failed', response.status, err.details);
  }
  // Prefer the server's Content-Disposition filename; fall back to the hint.
  const filename = filenameFromDisposition(response.headers.get('Content-Disposition'))
    || opts.filename
    || 'download';
  const blob = await response.blob();
  saveBlob(blob, filename);
}

/**
 * GET an endpoint and return its body as plain text — for copy-to-clipboard
 * exports (e.g. the Meta ZIP list, which is pasted into Meta, not saved). Sends
 * the auth cookie via credentials:'include'. Throws ApiError on non-2xx.
 *
 * @param {string} endpoint
 * @returns {Promise<string>}
 */
async function fetchText(endpoint) {
  const response = await fetch(apiUrl(endpoint), { credentials: 'include' });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(err.error || 'Request failed', response.status, err.details);
  }
  return response.text();
}

/**
 * Parse a filename out of a Content-Disposition header value (e.g.
 * `attachment; filename="my-map.pdf"`). Returns null if absent/unparseable.
 *
 * @param {string|null} disposition
 * @returns {string|null}
 */
function filenameFromDisposition(disposition) {
  if (!disposition) return null;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match ? match[1] : null;
}

export default api;
export { ApiError, apiUrl, downloadFromApi, postForDownload, fetchText, saveBlob };
